<template>
  <div class="main-content-container container-fluid px-4">
    <!-- Breadcrumbs -->
    <nav aria-label="breadcrumb" class="mb-3">
      <ol class="breadcrumb">
        <li class="breadcrumb-item"><a href="#" @click.prevent="$router.push({ name: 'insights' })">Insights</a></li>
        <li class="breadcrumb-item active">{{ insight.name || 'Insight' }}</li>
      </ol>
    </nav>

    <!-- Page Header -->
    <div class="page-header row no-gutters py-4">
      <div class="col-12 col-sm-4 text-center text-sm-left mb-0">
        <h3 class="page-title">{{ insight.name || 'Insight' }}</h3>
        <p v-if="insight.description" class="text-muted mb-0">{{ insight.description }}</p>
      </div>
      <div class="col-12 col-sm-8 text-center text-sm-right">
        <d-button theme="secondary" @click="editInsight">
          <i class="material-icons mr-1">edit</i> Edit
        </d-button>
        <d-button theme="primary" outline @click="refreshData">
          <i class="material-icons mr-1">refresh</i> Refresh
        </d-button>
      </div>
    </div>

    <!-- Time Range Selector -->
    <div class="row mb-4">
      <div class="col-md-12">
        <div class="card card-small">
          <div class="card-body">
            <div class="form-group mb-0">
              <label>Time Range</label>
              <div class="d-flex gap-2 flex-wrap">
                <input
                  v-model="startTime"
                  type="datetime-local"
                  class="form-control"
                  style="max-width: 250px;"
                />
                <input
                  v-model="endTime"
                  type="datetime-local"
                  class="form-control"
                  style="max-width: 250px;"
                />
                <d-button @click="updateTimeRange">Update</d-button>
                <d-button outline @click="setQuickRange('7d')">Last 7 Days</d-button>
                <d-button outline @click="setQuickRange('30d')">Last 30 Days</d-button>
                <d-button outline @click="setQuickRange('90d')">Last 90 Days</d-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Visualization -->
    <div class="row">
      <div class="col-12">
        <div class="card card-small mb-4">
          <div class="card-header border-bottom">
            <h6 class="m-0">Visualization</h6>
          </div>
          <div class="card-body">
            <insight-visualization
              :insight="insight"
              :start-time="startTimeISO"
              :end-time="endTimeISO"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Insight Configuration Summary -->
    <div class="row">
      <div class="col-md-6">
        <div class="card card-small mb-4">
          <div class="card-header border-bottom">
            <h6 class="m-0">Data Sources</h6>
          </div>
          <div class="card-body">
            <ul class="list-unstyled mb-0">
              <li v-for="(source, index) in insight.data_sources" :key="index" class="mb-2">
                <strong>{{ index + 1 }}.</strong> {{ formatDataSourceType(source.type) }}
                <span v-if="source.scenario" class="badge badge-info ml-2">{{ source.scenario }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <div class="card card-small mb-4">
          <div class="card-header border-bottom">
            <h6 class="m-0">Splits</h6>
          </div>
          <div class="card-body">
            <ul v-if="insight.splits && insight.splits.length > 0" class="list-unstyled mb-0">
              <li v-for="(split, index) in insight.splits" :key="index" class="mb-2">
                <strong>{{ index + 1 }}.</strong> {{ split.field }} ({{ split.type }})
              </li>
            </ul>
            <p v-else class="text-muted mb-0">No splits configured</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import insightsAPI from '@/api/insights';
import InsightVisualization from '@/components/insights/InsightVisualization.vue';
import moment from 'moment';

export default {
  name: 'InsightDetail',
  components: {
    InsightVisualization,
  },
  data() {
    return {
      insight: {
        name: '',
        description: '',
        type: '',
        data_sources: [],
        filters: [],
        splits: [],
      },
      startTime: moment().subtract(7, 'days').format('YYYY-MM-DDTHH:mm'),
      endTime: moment().format('YYYY-MM-DDTHH:mm'),
      loading: false,
    };
  },
  computed: {
    startTimeISO() {
      return moment(this.startTime).toISOString();
    },
    endTimeISO() {
      return moment(this.endTime).toISOString();
    },
  },
  created() {
    this.loadInsight();
  },
  methods: {
    loadInsight() {
      const id = parseInt(this.$route.params.id);
      this.loading = true;
      insightsAPI.getInsight(id)
        .then((response) => {
          this.insight = response.data;
        })
        .catch((error) => {
          console.error('Failed to load insight:', error);
          alert('Failed to load insight: ' + (error.response && error.response.data && error.response.data.error || error.message));
          this.$router.push({ name: 'insights' });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    editInsight() {
      this.$router.push({ name: 'insight_edit', params: { id: parseInt(this.$route.params.id) } });
    },
    refreshData() {
      // Trigger reload of visualization component
      this.$forceUpdate();
    },
    updateTimeRange() {
      // Time range is already bound, visualization will update automatically
      this.refreshData();
    },
    setQuickRange(range) {
      const end = moment();
      let start;
      switch (range) {
        case '7d':
          start = end.clone().subtract(7, 'days');
          break;
        case '30d':
          start = end.clone().subtract(30, 'days');
          break;
        case '90d':
          start = end.clone().subtract(90, 'days');
          break;
        default:
          start = end.clone().subtract(7, 'days');
      }
      this.startTime = start.format('YYYY-MM-DDTHH:mm');
      this.endTime = end.format('YYYY-MM-DDTHH:mm');
      this.updateTimeRange();
    },
    formatDataSourceType(type) {
      const types = {
        recommendation_requests: 'Recommendation Requests',
        recommended_items: 'Recommended Items',
        detail_views: 'Detail Views',
        purchases: 'Purchases',
        carts: 'Carts',
        detail_views_based_on_recommendations: 'Detail Views (Based on Recommendations)',
        purchases_based_on_recommendations: 'Purchases (Based on Recommendations)',
        carts_based_on_recommendations: 'Carts (Based on Recommendations)',
        ratio: 'Ratio',
      };
      return types[type] || type;
    },
  },
};
</script>

